import React, { lazy, Suspense, useCallback } from 'react';
import { useQuery } from 'react-query';
import { withChalkTalkErrorBoundary } from '@components/ChalkTalkErrorBoundary/ChalkTalkErrorBoundary';
import { ReportsPlaceholder } from '../ReportHarness/styles';
import { createGroupReportsRequest } from './GroupReportServiceAPI';
import {
  getGroupReportData,
  isDatasetReady,
  isDatasetGenerating,
  GroupReportDataResponse,
} from '@apis/districtReports';

const ReportsHarness = lazy(() =>
  import('../ReportHarness').then((module) => ({ default: module.default })),
);

interface SessionsSummaryByGroupReportProps {
  teacherId: string;
  reportId: string;
  districtId: number;
  curriculumSubjectId: number;
}

// Polling interval for dataset generation (5 seconds)
const POLLING_INTERVAL = 5000;

const SessionsSummaryByGroupReport = ({
  teacherId,
  reportId,
  districtId,
  curriculumSubjectId,
}: SessionsSummaryByGroupReportProps) => {
  const [widget, setWidget] = React.useState<any>(null);

  // Fetch the dataset_id and name_map from the backend with polling support
  const {
    data: groupReportData,
    isLoading,
    error,
    refetch,
  } = useQuery<GroupReportDataResponse>(
    ['groupReportData', districtId, curriculumSubjectId],
    () => getGroupReportData({ districtId, curriculumSubjectId }),
    {
      staleTime: 1000 * 60 * 5, // Consider stale after 5 minutes
      refetchOnWindowFocus: false,
      // Enable polling only when dataset is generating
      refetchInterval: (data) => {
        if (data && isDatasetGenerating(data)) {
          return POLLING_INTERVAL;
        }
        return false;
      },
      retry: 3,
    },
  );

  const reportData = React.useMemo(() => {
    if (!groupReportData || !isDatasetReady(groupReportData)) {
      return [];
    }

    return [createGroupReportsRequest(teacherId, groupReportData.dataset_id)];
  }, [teacherId, groupReportData]);

  // Callbacks for display name mapping (similar to PHP implementation)
  const callbacks = React.useMemo(() => {
    if (!groupReportData?.name_map) {
      return undefined;
    }

    return {
      displayNameListener: (originalNames: string[]) => {
        if (!Array.isArray(originalNames)) {
          return originalNames;
        }

        return originalNames.map(
          (name: string) => groupReportData.name_map?.[name] || name,
        );
      },
    };
  }, [groupReportData]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const renderReport =
    reportData.length > 0 &&
    !!reportData[0]?.reports?.[0]?.id &&
    !!reportData[0]?.reports?.[0]?.dataset_id;

  // Loading state
  if (isLoading) {
    return (
      <ReportsPlaceholder>
        <span>Loading group report data...</span>
      </ReportsPlaceholder>
    );
  }

  // Error state
  if (error) {
    return (
      <ReportsPlaceholder>
        <span>Error loading group report data. Please try again.</span>
        <button
          type="button"
          onClick={handleRetry}
          style={{ marginTop: '10px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </ReportsPlaceholder>
    );
  }

  // Dataset is generating - show progress
  if (groupReportData && isDatasetGenerating(groupReportData)) {
    const statusMessages: Record<string, string> = {
      pending: 'Preparing report data...',
      uploading: 'Uploading data to Learnosity...',
      processing: 'Generating report (this may take a minute)...',
    };

    return (
      <ReportsPlaceholder>
        <span>{statusMessages[groupReportData.status] || 'Generating report...'}</span>
        {groupReportData.user_count && groupReportData.user_count > 0 && (
          <span style={{ marginTop: '5px', fontSize: '0.9em', opacity: 0.8 }}>
            Processing data for {groupReportData.user_count.toLocaleString()} students
          </span>
        )}
      </ReportsPlaceholder>
    );
  }

  // Dataset generation failed
  if (groupReportData?.status === 'failed') {
    return (
      <ReportsPlaceholder>
        <span>Report generation failed: {groupReportData.error || 'Unknown error'}</span>
        <button
          type="button"
          onClick={handleRetry}
          style={{ marginTop: '10px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </ReportsPlaceholder>
    );
  }

  // No report data available
  if (!renderReport) {
    return (
      <ReportsPlaceholder>
        <span>No Group Report Available!</span>
      </ReportsPlaceholder>
    );
  }

  // Render the report
  return (
    <Suspense
      fallback={
        <ReportsPlaceholder>
          <span>Loading report...</span>
        </ReportsPlaceholder>
      }
    >
      <ReportsHarness
        key={reportId}
        widget={widget}
        userId={teacherId}
        setWidget={setWidget}
        reports={reportData[0].reports}
        reportId={reportId}
        callbacks={callbacks}
      />
    </Suspense>
  );
};

const wrappedSessionsSummaryByGroupReport = withChalkTalkErrorBoundary(
  SessionsSummaryByGroupReport,
);
export {
  wrappedSessionsSummaryByGroupReport as SessionsSummaryByGroupReport,
  SessionsSummaryByGroupReportProps,
};
